import { useState, useRef, useEffect } from 'react';
import { MdSend, MdHistory, MdClose, MdDelete, MdAdd, MdLocationOn, MdCheck, MdAttachFile, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, getDocs, doc, setDoc } from 'firebase/firestore';
import { db, functions } from './firebase';
import { httpsCallable } from "firebase/functions";

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function renderMarkdown(text) {
  if (!text) return '';
  let html = text;
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Lists
  html = html.replace(/^\* (.*?)$/gm, '<li>$1</li>');
  // Wrap consecutive li in ul
  html = html.replace(/(<li>.*?<\/li>\n?)+/g, match => `<ul>${match}</ul>`);
  // Paragraphs
  html = html.split(/\n\n+/).map(p => {
    if (p.trim().startsWith('<ul>')) return p;
    return `<p>${p}</p>`;
  }).join('');
  return html;
}

export default function ChatWindow({ onClose, isOpen }) {
  const defaultWelcomeMsg = { id: "1", text: "Hi! Welcome to Talisman Hire. I'm here to help you find the right equipment for your project, recommend suitable equipment, locate your nearest branch, answer your questions, and help you submit an enquiry. How can I help you today?", sender: "ai" };
  
  const [messages, setMessages] = useState([defaultWelcomeMsg]);
  const [inputValue, setInputValue] = useState("");
  const [chatId, setChatId] = useState(null);
  const [isLiveAgent, setIsLiveAgent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [openPhases, setOpenPhases] = useState({});
  const togglePhase = (phaseId) => {
    setOpenPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  const [userLocationContext, setUserLocationContext] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [locationSuccess, setLocationSuccess] = useState(false);
  const [branchesData, setBranchesData] = useState([]);
  const [productsKnowledge, setProductsKnowledge] = useState([]);
  const [catalogDataState, setCatalogDataState] = useState([]);
  
  // History State
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const messagesEndRef = useRef(null);

  const latestMessagesRef = useRef(messages);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  useEffect(() => {
    if (isOpen && inputRef.current && !isLoading) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    latestMessagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // 1. Initial Load of Sessions and JSON data
  useEffect(() => {
    const initializeData = async () => {
      let fetchedBranches = [];
      let fetchedProducts = [];
      
      try {
        // Load branches from Firestore
        const branchesSnapshot = await getDocs(collection(db, 'branches'));
        fetchedBranches = branchesSnapshot.docs.map(doc => doc.data());
        
        if (fetchedBranches.length === 0) {
          const localRes = await fetch('https://talisman-ai-agent-9cb94.web.app/branches.json');
          fetchedBranches = await localRes.json();
        }
        setBranchesData(fetchedBranches);
      } catch (e) {
        console.error('Error fetching branches:', e);
      }

      // Removed products knowledge fetch to save bandwidth
        
      const saved = localStorage.getItem('talisman_chat_history');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setChatSessions(parsed);
          if (parsed.length > 0) {
            const latest = parsed.sort((a,b) => b.updatedAt - a.updatedAt)[0];
            const THIRTY_MINUTES = 30 * 60 * 1000;
            if (Date.now() - latest.updatedAt > THIRTY_MINUTES) {
              startNewSession(fetchedBranches, fetchedProducts);
            } else {
              setCurrentSessionId(latest.id);
              setMessages(latest.messages);
              setIsLiveAgent(latest.isLiveAgent || false);
              setChatId(latest.liveChatId || null);
              initChat(latest.messages, fetchedBranches, fetchedProducts);
            }
          } else {
            startNewSession(fetchedBranches, fetchedProducts);
          }
        } catch(e) {
          startNewSession(fetchedBranches, fetchedProducts);
        }
      } else {
        startNewSession(fetchedBranches, fetchedProducts);
      }
    };
    initializeData();
  }, []);

  // 2. Save current session to LocalStorage on messages change
  useEffect(() => {
    scrollToBottom();
    
    if (currentSessionId && messages.length > 1) { // Don't save empty sessions with just the welcome message
      setChatSessions(prev => {
        const updated = [...prev];
        const idx = updated.findIndex(s => s.id === currentSessionId);
        
        const titleText = messages.find(m => m.sender === 'user')?.text || "New Conversation";
        const title = titleText.length > 30 ? titleText.substring(0, 30) + '...' : titleText;
        
        const sessionData = { 
          id: currentSessionId, 
          messages, 
          title, 
          updatedAt: Date.now(),
          isLiveAgent,
          liveChatId: chatId
        };

        if (idx >= 0) {
          updated[idx] = sessionData;
        } else {
          updated.unshift(sessionData);
        }
        
        localStorage.setItem('talisman_chat_history', JSON.stringify(updated));
        return updated.sort((a,b) => b.updatedAt - a.updatedAt);
      });
    }
  }, [messages, currentSessionId, isLiveAgent, chatId]);

  // Listen to live chat if transferred to agent
  useEffect(() => {
    if (!chatId || !isLiveAgent) return;
    
    const q = query(collection(db, `chats/${chatId}/messages`), orderBy("timestamp"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMsgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (newMsgs.some(m => m.isResolvedMessage)) {
        setIsLiveAgent(false);
      }
      
      setMessages(prev => {
        const merged = [...prev];
        newMsgs.forEach(n => {
          if (!merged.find(m => m && m.id && n && n.id && m.id.toString() === n.id.toString())) merged.push(n);
        });
        const getTime = (t) => {
          if (!t) return 0;
          if (typeof t.toMillis === 'function') return t.toMillis();
          if (typeof t.getTime === 'function') return t.getTime();
          return 0;
        };
        
        const sorted = merged.sort((a,b) => getTime(a.timestamp) - getTime(b.timestamp));
        return sorted;
      });
    });
    
    return () => unsubscribe();
  }, [chatId, isLiveAgent]);

  const initChat = async (historyMsgs = [], bData = branchesData, pKnowledge = productsKnowledge) => {
    setIsLoading(true);
    try {
      let catalogData = [];
      const cached = localStorage.getItem('talisman_catalog_v4');
      const cachedTime = localStorage.getItem('talisman_catalog_time_v4');
      const now = Date.now();
      
      if (cached && cachedTime && (now - parseInt(cachedTime)) < 1000 * 60 * 60) {
        catalogData = JSON.parse(cached);
      }
      
      if (!catalogData || catalogData.length === 0) {
        const snapshot = await getDocs(collection(db, 'products'));
        catalogData = snapshot.docs.map(doc => doc.data());
        if (catalogData.length > 0) {
          localStorage.setItem('talisman_catalog_v4', JSON.stringify(catalogData));
          localStorage.setItem('talisman_catalog_time_v4', now.toString());
        }
      }
      setCatalogDataState(catalogData);
      
    } catch (error) {
      console.error("Failed to initialize AI:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewSession = async (bData = branchesData, pKnowledge = productsKnowledge) => {
    const newId = Date.now().toString();
    setCurrentSessionId(newId);
    setMessages([defaultWelcomeMsg]);
    setIsLiveAgent(false);
    setChatId(null);
    setIsLoading(false);
    setIsSidebarOpen(false);
    initChat([], bData, pKnowledge);
  };

  const switchSession = (sessionId) => {
    const session = chatSessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSessionId(session.id);
      setMessages(session.messages);
      setIsLiveAgent(session.isLiveAgent || false);
      setChatId(session.liveChatId || null);
      setIsLoading(false);
      if (!session.isLiveAgent) {
        initChat(session.messages);
      }
    }
    setIsSidebarOpen(false);
  };

  const deleteSession = (e, sessionId) => {
    e.stopPropagation(); // prevent switching to it
    const updated = chatSessions.filter(s => s.id !== sessionId);
    setChatSessions(updated);
    localStorage.setItem('talisman_chat_history', JSON.stringify(updated));
    
    if (currentSessionId === sessionId) {
      if (updated.length > 0) {
        switchSession(updated[0].id);
      } else {
        startNewSession();
      }
    }
  };

  const handleSend = async (textOverride = null, isSilent = false) => {
    const textToSend = typeof textOverride === 'string' ? textOverride : inputValue;
    const userText = textToSend.trim();
    if ((!userText && !selectedFile) || isLoading) return;
    
    const msgId = Date.now().toString();
    const userTimestamp = new Date();
    
    const fileToSend = selectedFile;
    
    if (!isSilent) {
      setMessages(prev => [...prev, { 
        id: msgId, 
        text: userText, 
        sender: "user", 
        timestamp: userTimestamp,
        fileName: fileToSend ? fileToSend.name : null
      }]);
    }
    
    if (typeof textOverride !== 'string') {
      setInputValue("");
      setSelectedFile(null);
    }

    if (isLiveAgent && chatId) {
      const payload = {
        text: userText,
        sender: "user",
        timestamp: userTimestamp
      };
      if (fileToSend) payload.attachedFile = fileToSend.name;
      
      await setDoc(doc(db, `chats/${chatId}/messages`, msgId), payload);
      return;
    }

    setIsLoading(true);

    // Gemini AI Logic
    const callAI = async (currentHistoryMsgs, newMsgContent, contextOverride = userLocationContext) => {
      if (catalogDataState.length === 0) {
        await initChat();
      }
      let formattedHistory = [];
      const validMsgs = currentHistoryMsgs.filter(m => m.id !== 1 && m.sender !== 'system');
      for (const m of validMsgs) {
        const role = m.sender === 'user' ? 'user' : 'model';
        if (formattedHistory.length === 0) {
          if (role === 'user') formattedHistory.push({ role, parts: [{ text: m.text }] });
        } else {
          const lastMsg = formattedHistory[formattedHistory.length - 1];
          if (lastMsg.role === role) {
            if (lastMsg.parts && lastMsg.parts.length > 0) { lastMsg.parts[0].text += '\n' + (m.text || ''); }
          } else {
            formattedHistory.push({ role, parts: [{ text: m.text }] });
          }
        }
      }
      
      const generateAIResponse = httpsCallable(functions, 'generateAIResponse');
      
      const result = await generateAIResponse({
        history: formattedHistory.length > 0 ? formattedHistory : [{ role: 'user', parts: [{ text: "Hello" }] }],
        message: newMsgContent,
        locationContext: contextOverride
      });
      return result.data.text;
    };

    try {
      let contentToSend = userText || "Please analyze this file.";
      if (fileToSend) {
        const base64StringWithPrefix = await fileToBase64(fileToSend);
        const base64Data = base64StringWithPrefix.split(',')[1];
        contentToSend = [
          { text: userText || "Please analyze this file." },
          { inlineData: { data: base64Data, mimeType: fileToSend.type } }
        ];
      }

      console.log("Sending to Gemini via Backend.");
      let responseText = await callAI(messages, contentToSend);
      
      // Clean up markdown code block formatting if Gemini wrapped the JSON
      responseText = responseText.replace(/^```json\s*/i, '').replace(/\s*```$/i, '').trim();
      
      let parsed = JSON.parse(responseText);

      // Auto-location logic
      if (parsed.needs_location) {
        if (!navigator.geolocation) {
          let fallbackText = await callAI(messages, "System: Geolocation is not supported by this browser. Please ask the user to type their suburb or city manually.");
          fallbackText = fallbackText.replace(/^```json\s*/i, '').replace(/\s*```$/i, '').trim();
          parsed = JSON.parse(fallbackText);
        } else {
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { 
                enableHighAccuracy: true, 
                timeout: 10000, 
                maximumAge: 0 
              });
            });
            const userLat = position.coords.latitude;
            const userLng = position.coords.longitude;
            
            const branchesWithDistance = branchesData
              .filter(b => b.lat && b.lng)
              .map(b => ({
                ...b,
                distance: haversineDistance(userLat, userLng, b.lat, b.lng)
              }))
              .sort((a, b) => a.distance - b.distance);
              
            const closest = branchesWithDistance.slice(0, 3);
            const contextStr = `The user has shared their exact location. The 3 closest branches to the user are: \n` + 
              closest.map((b, i) => `${i+1}. ${b.name} (${b.distance.toFixed(1)} km away)`).join('\n') +
              `\nIf the user asks for the nearest branch, YOU MUST recommend the closest one from this list.`;
              
            setUserLocationContext(contextStr);
            
            // Silently re-send to get the final answer with location context
            let newText = await callAI(messages, `System: Location granted. ${contextStr}`, contextStr);
            newText = newText.replace(/^```json\s*/i, '').replace(/\s*```$/i, '').trim();
            parsed = JSON.parse(newText);
            
          } catch (err) {
            console.error("Location error:", err);
            let fallbackText = await callAI(messages, "System: The user denied location access. Please politely ask them to type their suburb or city manually instead.");
            fallbackText = fallbackText.replace(/^```json\s*/i, '').replace(/\s*```$/i, '').trim();
            parsed = JSON.parse(fallbackText);
          }
        }
      }

      let aiResponseText = parsed.response || "I didn't quite catch that.";
      let aiActions = parsed.actions || [];
      // Backward compatibility for single action
      if (parsed.action) aiActions.push(parsed.action);

      // Intercept equipment inquiries silently
      const equipmentAction = aiActions.find(a => a.type === 'equipment_inquiry');
      if (equipmentAction && equipmentAction.payload) {
        try {
          const payload = equipmentAction.payload;
          const equipmentName = typeof payload === 'string' ? payload : payload.equipment;
          const enquiryType = typeof payload === 'object' && payload.enquiryType ? payload.enquiryType : 'General';
          const branchId = typeof payload === 'object' && payload.branchId ? payload.branchId : null;

          await addDoc(collection(db, "equipment_analytics"), {
            equipment: equipmentName,
            enquiryType: enquiryType,
            branchId: branchId,
            timestamp: serverTimestamp()
          });
        } catch (e) {
          console.error("Failed to log equipment analytics:", e);
        }
      }

      // Detect agent handoff
      const agentHandoffAction = aiActions.find(a => a.type === 'transfer_to_agent');
      if (agentHandoffAction) {
         setIsLiveAgent(true);
         const payload = agentHandoffAction.payload || {};
         const customerName = payload.name || "Website Visitor";
         const customerPhone = payload.phone || "";
         const customerEmail = payload.email || "";
         const handoffBranchId = payload.branchId || null;
         const chatSummary = payload.summary || "";
         
         const chatDoc = await addDoc(collection(db, "chats"), {
           status: "active",
           createdAt: serverTimestamp(),
           customerName: customerName,
           customerPhone: customerPhone,
           customerEmail: customerEmail,
           branchId: handoffBranchId,
           summary: chatSummary
         });
         setChatId(chatDoc.id);
         
         messages.forEach(async (m) => {
           await setDoc(doc(db, `chats/${chatDoc.id}/messages`, m.id.toString()), {
             text: m.text,
             sender: m.sender,
             timestamp: m.timestamp || new Date()
           });
         });
         
         if (!isSilent) {
           const payload = {
             text: userText,
             sender: "user",
             timestamp: userTimestamp
           };
           if (fileToSend) payload.attachedFile = fileToSend.name;
           await setDoc(doc(db, `chats/${chatDoc.id}/messages`, msgId), payload);
         }
         
         let aiMsgId = (Date.now() + 1).toString();
         let aiTimestamp = new Date(Date.now() + 100);
         await setDoc(doc(db, `chats/${chatDoc.id}/messages`, aiMsgId), {
           text: aiResponseText,
           sender: "system",
           timestamp: aiTimestamp
         });
         
         setMessages(prev => {
           if (prev.find(m => m.id.toString() === aiMsgId)) return prev;
           return [...prev, { 
             id: aiMsgId, 
             text: aiResponseText, 
             sender: "system", 
             timestamp: aiTimestamp,
             actions: aiActions
           }];
         });
      } else {
        setMessages(prev => [...prev, { 
          id: Date.now().toString(), 
          text: aiResponseText, 
          sender: isLiveAgent ? "system" : "ai", 
          timestamp: new Date(),
          actions: aiActions
        }]);
      }

    } catch (err) {
      console.error("Gemini Error:", err);
      try {
        await addDoc(collection(db, "chat_errors"), {
          error: err.message,
          stack: err.stack || "",
          timestamp: serverTimestamp(),
          chatId: chatId || "new",
          userAgent: navigator.userAgent
        });
      } catch (logErr) {
        console.error("Failed to log error to Firestore:", logErr);
      }
      setMessages(prev => [...prev, { 
        id: Date.now().toString(), 
        text: `Sorry, I'm having trouble processing your request at the moment. Please try again or contact a branch directly.`, 
        sender: "system", 
        timestamp: new Date() 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="talisman-chat-window">
      
      {/* History Sidebar */}
      <div className={`chat-history-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="history-header">
          <h3>Chat History</h3>
          <button className="icon-btn" style={{ color: 'var(--text-dark)' }} onClick={() => setIsSidebarOpen(false)}>
            <MdClose />
          </button>
        </div>
        <button className="new-chat-btn" onClick={startNewSession}>
          <MdAdd size={20} /> New Conversation
        </button>
        <div className="history-list">
          {chatSessions.map(session => (
            <div 
              key={session.id} 
              className="history-item" 
              onClick={() => switchSession(session.id)}
              style={{ background: currentSessionId === session.id ? '#fff3f3' : 'transparent' }}
            >
              <div className="history-item-content">
                <div className="history-item-title">{session.title} {session.isLiveAgent && '🎧'}</div>
                <div className="history-item-date">{new Date(session.updatedAt).toLocaleDateString()}</div>
              </div>
              <button 
                className="history-delete-btn"
                onClick={(e) => deleteSession(e, session.id)}
                title="Delete Conversation"
              >
                <MdDelete size={18} />
              </button>
            </div>
          ))}
          {chatSessions.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-light)', fontSize: '13px' }}>
              No past conversations
            </div>
          )}
        </div>
      </div>

      <div className="chat-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>TMan</h2>
            <p>{isLiveAgent ? <span key="agent">Online • Speaking with Agent</span> : <span key="ai">Online • Ready to help</span>}</p>
          </div>
          <div className="chat-header-actions">
            <button className="icon-btn" onClick={() => setIsSidebarOpen(true)} title="History">
              <MdHistory />
            </button>
          </div>
        </div>
      </div>
      
      <div className="chat-messages">
        {messages.map((msg, index) => {
          if (!msg) return null;
          return (
          <div key={msg.id || `fallback-${index}`} className={`message ${msg.sender || 'system'}`}>
            {msg.fileName && (
              <div style={{ fontSize: '12px', background: 'rgba(0,0,0,0.05)', padding: '6px 10px', borderRadius: '6px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MdAttachFile size={14} /> {msg.fileName}
              </div>
            )}
            {msg.text && (
              <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
            )}
            {msg.actions && msg.actions.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%' }}>
                {msg.actions.map((act, idx) => {
                  if (act.type === 'open_link') {
                    const product = catalogDataState.find(p => p.url === act.payload);
                    const finalImageUrl = act.imageUrl || (product ? product.imageUrl : null);
                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                        {finalImageUrl && (
                          <img 
                            src={finalImageUrl} 
                            alt={act.label || 'Product image'} 
                            style={{ width: '100%', height: '160px', objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'white', padding: '8px', boxSizing: 'border-box' }}
                          />
                        )}
                        {(() => {
                          const product = catalogDataState.find(p => p.url === act.payload);
                          if (product && product.selectedStoresOnly) {
                            return (
                              <div style={{ color: '#d32f2f', fontSize: '13px', fontWeight: 'bold', textAlign: 'center', marginTop: '4px' }}>
                                * Available at selected stores
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <a href={act.payload} className="ai-action-btn" style={{ textDecoration: 'none', width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
                          {act.label || 'Open Link'}
                        </a>
                      </div>
                    );
                  } else if (act.type === 'branch_card') {
                    const branch = branchesData.find(b => b.id === act.payload);
                    if (!branch) return null;
                    
                    return (
                      <div key={idx} className="branch-card">
                        {branch.imageUrl && (
                          <div className="branch-card-img" style={{ backgroundImage: `url(${branch.imageUrl})` }}></div>
                        )}
                        <div className="branch-card-content">
                          <h4>{branch.name}</h4>
                          <p>
                            <strong>Address:</strong>{' '}
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.name + ' ' + branch.address)}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: 'var(--primary-red)', textDecoration: 'underline' }}
                            >
                              {branch.address}
                            </a>
                          </p>
                          <p>
                            <strong>Phone:</strong>{' '}
                            <a 
                              href={`tel:${branch.phone.replace(/\s+/g, '')}`} 
                              style={{ color: 'var(--primary-red)', textDecoration: 'underline' }}
                            >
                              {branch.phone}
                            </a>
                          </p>
                          <div className="branch-hours">
                            <p><strong>Weekdays:</strong> {branch.hours?.weekdays}</p>
                            <p><strong>Saturday:</strong> {branch.hours?.saturday}</p>
                            <p><strong>Sunday:</strong> {branch.hours?.sunday}</p>
                          </div>
                          <button 
                            className="ai-action-btn branch-chat-btn"
                            onClick={() => {
                              const now = new Date();
                              const timeStr = now.toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg", weekday: 'long', hour: '2-digit', minute:'2-digit' });
                              
                              const prompt = `System: The user clicked 'Chat with Branch' for branch ID ${branch.id} (${branch.name}). 
The current local time in South Africa is ${timeStr}.
Branch Operating Hours:
- Weekdays: ${branch.hours?.weekdays || 'Unknown'}
- Saturday: ${branch.hours?.saturday || 'Unknown'}
- Sunday: ${branch.hours?.sunday || 'Unknown'}

Please determine if the branch is currently open based on the day and time. (Note: If today is a South African public holiday, mention that hours may differ).
If the branch is currently CLOSED:
1. Inform the user upfront that the branch is currently closed and will contact them when they open.
2. Ask for their Name, Phone number, and Email address.

If the branch is currently OPEN:
1. Ask for their Name, Phone number, and Email address to pass on to the branch.

Once they provide their details, output the transfer_to_agent action with their details in the payload.`;
                              
                              handleSend(prompt, true);
                            }}
                          >
                            Chat with Branch
                          </button>
                        </div>
                      </div>
                    );
                  } else if (act.type === 'choices' && act.options) {
                    return act.options.map((option, oIdx) => (
                      <button key={`${idx}-${oIdx}`} className="ai-action-btn" style={{ background: 'white', color: 'var(--primary-red)', border: '1px solid var(--primary-red)' }} onClick={() => handleSend(option)}>
                        {option}
                      </button>
                    ));
                  } else if (act.type === 'plan_phases' && Array.isArray(act.payload)) {
                    return act.payload.map((phase, pIdx) => {
                      const phaseId = `${msg.id}-${idx}-${pIdx}`;
                      const isOpen = !!openPhases[phaseId];
                      return (
                        <div key={phaseId} className="plan-phase-accordion" style={{ width: '100%', marginBottom: '8px' }}>
                          <div className="plan-phase-header" onClick={() => togglePhase(phaseId)}>
                            <h4 style={{ margin: 0, fontSize: '15px' }}>{phase.title}</h4>
                            {isOpen ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
                          </div>
                          {isOpen && phase.equipment && Array.isArray(phase.equipment) && (
                            <div className="plan-phase-content">
                              {phase.equipment.map((eq, eqIdx) => {
                                if (eq.type !== 'open_link') return null;
                                const subProduct = catalogDataState.find(p => p.url === eq.payload);
                                const finalSubImageUrl = subProduct ? subProduct.imageUrl : null;
                                return (
                                  <div key={eqIdx} style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', paddingBottom: '16px' }}>
                                    {finalSubImageUrl && (
                                      <img 
                                        src={finalSubImageUrl} 
                                        alt={eq.label || 'Product image'} 
                                        style={{ width: '100%', height: '160px', objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'white', padding: '8px', boxSizing: 'border-box' }}
                                      />
                                    )}
                                    {(() => {
                                      const product = catalogDataState.find(p => p.url === eq.payload);
                                      if (product && product.selectedStoresOnly) {
                                        return (
                                          <div style={{ color: '#d32f2f', fontSize: '13px', fontWeight: 'bold', textAlign: 'center', marginTop: '4px' }}>
                                            * Available at selected stores
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    <a href={eq.payload} className="ai-action-btn" style={{ textDecoration: 'none', width: '100%', boxSizing: 'border-box', textAlign: 'center' }} target="_blank" rel="noopener noreferrer">
                                      {eq.label || 'Open Link'}
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  }
                  return null;
                })}
              </div>
            )}
          </div>
          );
        })}
        {isLoading && (
          <div className="message ai">
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-input-container" style={{ display: 'flex', flexDirection: 'column', padding: '16px', background: 'white', borderTop: '1px solid var(--border-color)', borderBottomRightRadius: '12px', borderBottomLeftRadius: '12px' }}>
        {selectedFile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'rgba(0,0,0,0.05)', borderRadius: '6px', marginBottom: '8px', alignSelf: 'flex-start', fontSize: '13px' }}>
            <MdAttachFile size={14} /> 
            <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</span>
            <button onClick={() => setSelectedFile(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><MdClose size={14} /></button>
          </div>
        )}
        <div className="chat-input-area" style={{ display: 'flex', gap: '8px', padding: '0', border: 'none' }}>
          <input 
            type="file" 
            ref={fileInputRef} 
            hidden 
            accept=".jpg,.jpeg,.png,.pdf" 
            onChange={handleFileSelect} 
          />
          <button 
            className="icon-btn" 
            onClick={() => fileInputRef.current?.click()} 
            disabled={isLoading}
            style={{ padding: '0 8px', color: 'var(--text-light)', background: 'transparent', border: 'none' }}
            title="Attach building/site plan"
          >
            <MdAttachFile size={22} />
          </button>
          <input 
            ref={inputRef}
            type="text" 
            placeholder={!isLiveAgent ? "Type your message..." : "Type your message to the agent..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            disabled={isLoading}
            style={{ flex: 1, padding: '12px', border: '1px solid var(--border-color)', borderRadius: '24px', outline: 'none', fontSize: '14px' }}
          />
          <button onClick={() => handleSend()} disabled={(!inputValue.trim() && !selectedFile) || isLoading} style={{ background: 'var(--primary-red)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (!inputValue.trim() && !selectedFile) ? 'not-allowed' : 'pointer', opacity: (!inputValue.trim() && !selectedFile) ? 0.5 : 1 }}>
            <MdSend size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
